import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, GripVertical, ArrowUp, ArrowDown } from "lucide-react";

const COLUMN_TYPES = [
  { value: "number", label: "Number" },
  { value: "currency", label: "Currency ($)" },
  { value: "percent", label: "Percent (%)" },
  { value: "text", label: "Text box" },
  { value: "notes", label: "Notes (long text)" },
  { value: "checkbox", label: "Checkbox" },
];

export default function ReportConfig() {
  const [columns, setColumns] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  useEffect(() => {
    api.get("/grid-config").then((r) => setColumns(r.data.columns)).catch(() => setColumns([]));
  }, []);

  const update = (fn) => setColumns((c) => fn(JSON.parse(JSON.stringify(c))));
  const add = () => update((c) => [...c, { key: `col_${Date.now().toString(36)}`, label: "New Column", type: "currency" }]);
  const remove = (i) => update((c) => c.filter((_, idx) => idx !== i));
  const move = (i, dir) =>
    update((c) => {
      const j = i + dir;
      if (j < 0 || j >= c.length) return c;
      [c[i], c[j]] = [c[j], c[i]];
      return c;
    });
  const reorder = (from, to) =>
    update((c) => {
      if (from == null || to == null || from === to || to < 0 || to >= c.length) return c;
      const [item] = c.splice(from, 1);
      c.splice(to, 0, item);
      return c;
    });
  const setSeq = (i, val) => {
    const pos = parseInt(val, 10);
    if (isNaN(pos)) return;
    reorder(i, Math.max(0, Math.min(pos - 1, columns.length - 1)));
  };
  const setCol = (i, k, v) => update((c) => { c[i][k] = v; return c; });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/grid-config", { columns });
      toast.success("Report columns saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (columns === null) {
    return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  return (
    <div className="space-y-6 pb-16" data-testid="report-config-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-caps text-primary">Configuration</div>
          <h1 className="font-heading text-3xl font-black tracking-tight lg:text-4xl">Report Builder</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            These columns power BOTH the Monthly Report grid and the Daily Report form, plus the Excel export.
            Rename, retype, add or remove columns. Drag the handle to reorder, or type a new position in the # box.
            Use a <strong>Checkbox</strong> (e.g. to verify a deposit) or a <strong>Notes</strong> field for comments.
            Changes apply going forward; historical data is preserved.
          </p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-columns-btn">
          {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />} Save Columns
        </Button>
      </div>

      <Card className="rounded-none border">
        <div className="grid grid-cols-12 gap-2 border-b border-border bg-secondary px-4 py-2 label-caps text-muted-foreground">
          <div className="col-span-2">#</div>
          <div className="col-span-5">Column Name</div>
          <div className="col-span-3">Type</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        <div className="divide-y divide-border">
          {columns.map((c, i) => (
            <div
              key={i}
              className={`grid grid-cols-12 items-center gap-2 px-4 py-2 transition-colors ${overIndex === i && dragIndex !== null ? "bg-primary/10" : ""}`}
              data-testid={`col-row-${i}`}
              onDragOver={(e) => { e.preventDefault(); setOverIndex(i); }}
              onDrop={(e) => { e.preventDefault(); reorder(dragIndex, i); setDragIndex(null); setOverIndex(null); }}
            >
              <div className="col-span-2 flex items-center gap-1 text-xs text-muted-foreground">
                <span
                  draggable
                  onDragStart={() => setDragIndex(i)}
                  onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                  className="cursor-grab active:cursor-grabbing"
                  data-testid={`col-drag-${i}`}
                >
                  <GripVertical className="h-4 w-4" />
                </span>
                <Input
                  type="number"
                  value={i + 1}
                  onChange={(e) => setSeq(i, e.target.value)}
                  className="h-8 w-12 px-1 text-center text-xs"
                  data-testid={`col-seq-${i}`}
                />
              </div>
              <Input className="col-span-5" value={c.label} onChange={(e) => setCol(i, "label", e.target.value)} data-testid={`col-label-${i}`} />
              <div className="col-span-3">
                <Select value={c.type} onValueChange={(v) => setCol(i, "type", v)}>
                  <SelectTrigger data-testid={`col-type-${i}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{COLUMN_TYPES.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2 flex justify-end gap-1">
                <Button size="icon" variant="ghost" onClick={() => move(i, -1)} data-testid={`col-up-${i}`}><ArrowUp className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => move(i, 1)} data-testid={`col-down-${i}`}><ArrowDown className="h-4 w-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(i)} data-testid={`col-remove-${i}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Button variant="outline" onClick={add} data-testid="add-column-btn">
        <Plus className="mr-2 h-4 w-4" /> Add Column
      </Button>
    </div>
  );
}
